import React, { useEffect, useState, useTransition } from 'react';
import { User, Country, City } from 'src/types';
import { FiMenu, FiX } from 'react-icons/fi';
import { AppRoutes, AdminRoutes } from 'src/lib/routes';
import classNames from 'classnames';
import ClientOnlyPortal from 'src/components/ClientOnlyPortal';
import useBreakpoints from 'src/hooks/useBreakpoints';
import Destinations from 'src/components/Destinations';
import Link from 'next/link';
import useLogout from 'src/hooks/useLogout';
import { useRouter } from 'next/router';
import { FiChevronRight, FiChevronLeft } from 'react-icons/fi';
import { AboutItems } from 'src/components/AboutDropdown';
import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock';
import useCurrentCurrency from 'src/hooks/useCurrentCurrency';
import { ListCurrency } from 'src/components/CurrencySelector';
import useCheckUnreadMessage from 'src/hooks/useCheckUnreadMessage';
import { GiftItems } from './GiftDropdown';
import useInitialData from 'src/hooks/useInitialData';
import interactionResponse from 'src/lib/interaction_response';
import { useTranslations } from 'next-intl';
import LocaleSelector from 'src/components/LocaleSelector';

type Props = {
  currentUser?: User | undefined;
  cities: Record<number, City[]>;
  countries: Country[];
};

type SidenavProps = Props;

const MobileSidenavAndButton: React.FC<SidenavProps> = ({ currentUser, cities, countries }) => {
  const translation = useTranslations('components.Header')
  const [, startTransition] = useTransition()
  const { isScreenLg } = useBreakpoints();
  const [showSidenav, setShowSidenav] = useState(false);
  const userSignedIn = !!currentUser;
  const [isSelectedCountry, setIsSelectedCountry] = useState(false);
  const [isSelectedAbout, setIsSelectedAbout] = useState(false);
  const [isSelectedGift, setIsSelectedGift] = useState(false);
  const [isSelectedProfile, setIsSelectedProfile] = useState(false);
  const [isSelectedCurrency, setIsSelectedCurrency] = useState(false);
  const currentCurrency = useCurrentCurrency();
  const logout = useLogout();
  const router = useRouter();
  const initialData = useInitialData();
  const hasUnreadMessage = useCheckUnreadMessage(showSidenav)

  const close = () => {
    setShowSidenav(false);
  };

  useEffect(() => {
    if (router.isReady) {
      const toggle = () => {
        setShowSidenav(false);
        const sideNavContent = document.querySelector('.sidenav__content');
        if (sideNavContent) {
          enableBodyScroll(sideNavContent);
        }
      };
      router.events.on('routeChangeComplete', toggle);

      return () => {
        router.events.off('routeChangeComplete', toggle);
      };
    }
  }, [router]);

  const loginPath = AppRoutes.loginRoute(
    {},
    router.pathname === AppRoutes.loginRoute().toPath()
      ? {}
      : { redirect_path: router.pathname, redirect_path_query: JSON.stringify(router.query) }
  ).toPath();

  return isScreenLg ? null : (
    <>
      <button
        className="tw-block tw-px-0 tw-outline-none tw-bg-transparent tw-text-current"
        type="button"
        aria-label="Toggle navigation"
        onClick={async () => {
          setShowSidenav(prev => !prev);

          const sideNavContent = document.querySelector('.sidenav__content');
          if (showSidenav) {
            startTransition(() => {
              setIsSelectedCountry(false);
              setIsSelectedAbout(false);
              setIsSelectedGift(false);
              setIsSelectedProfile(false);
              setIsSelectedCurrency(false);
            });

            await interactionResponse()
            if (sideNavContent) {
              enableBodyScroll(sideNavContent);
            }
          } else {
            await interactionResponse()
            if (sideNavContent) {
              disableBodyScroll(sideNavContent, {
                allowTouchMove: (el: HTMLElement | Element) => {
                  return !!el.closest('#modal-select-langague-currency');
                }
              });
            }
          }
        }}
      >
        {showSidenav ? (
          <FiX size={28} />
        ) : (
          <FiMenu size={28} />
        )}
      </button>
      <ClientOnlyPortal selector="#modal-sidenav">
        <div
          className={classNames('sidenav', { active: showSidenav })}
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            if (e.target instanceof HTMLElement) {
              if (
                e.target.closest('#modal-select-langague-currency') ||
                e.target.closest('.sidenav__content') ||
                e.target.classList.contains('sidenav__content')
              ) {
                return;
              } else {
                close();
              }
            }
          }}
        >
          <div className="sidenav__inner">
            {/* the menu sits on top of the header's shadow, so it draws the shadow itself */}
            <div className="sidenav__content tw-shadow-top">
              <div className="sidenav__body">
                <div className="sidenav__section">
                  <div
                    className={classNames('tw-p-4', {
                      'tw-hidden':
                        isSelectedAbout ||
                        isSelectedCurrency ||
                        isSelectedGift ||
                        isSelectedProfile,
                    })}
                  >
                    <div className='tw-mt-4'>
                      <LocaleSelector showLanguageName={true} />
                    </div>
                    <div className="tw-border-b tw-border-grey-lighter tw-border-solid tw-my-5"></div>
                    <Destinations
                      cities={cities}
                      key={showSidenav ? 1 : 0 }
                      countries={countries}
                      onSelect={(country) => {
                        setIsSelectedCountry(country ? true : false);
                      }}
                    />
                  </div>
                  <div className={classNames('tw-p-4', { 'tw-hidden': !isSelectedAbout })}>
                    <div
                      className="tw-flex tw-items-center tw-cursor-pointer hover:tw-font-semibold tw-mb-6"
                      onClick={() => {
                        setIsSelectedAbout(false);
                      }}
                    >
                      <FiChevronLeft size={18} className="tw-mr-2 tw-align-top" />
                      <span className="tw-text-sm tw-leading-4">{translation('backToHome')}</span>
                    </div>

                    <div className="tw-border-t tw-border-grey-lighter tw-mb-6"></div>

                    <div className="tw-font-semibold tw-font-styleSans tw-text-2xl tw-text-brand-navy tw-mb-3 tw-leading-none">
                      {translation('aboutUs')}
                    </div>

                    <AboutItems />
                  </div>
                  <div className={classNames('tw-p-4', { 'tw-hidden': !isSelectedGift })}>
                    <div
                      className="tw-flex tw-items-center tw-cursor-pointer hover:tw-font-semibold tw-mb-6"
                      onClick={() => {
                        setIsSelectedGift(false);
                      }}
                    >
                      <FiChevronLeft size={18} className="tw-mr-2 tw-align-top" />
                      <span className="tw-text-sm tw-leading-4">{translation('backToHome')}</span>
                    </div>

                    <div className="tw-border-t tw-border-grey-lighter tw-mb-6"></div>
                    <GiftItems />
                  </div>
                  <div className={classNames('tw-p-4', { 'tw-hidden': !isSelectedProfile })}>
                    <div
                      className="tw-flex tw-items-center tw-cursor-pointer hover:tw-font-semibold tw-mb-6"
                      onClick={() => {
                        setIsSelectedProfile(false);
                      }}
                    >
                      <FiChevronLeft size={18} className="tw-mr-2 tw-align-top" />
                      <span className="tw-text-sm tw-leading-4">{translation('backToHome')}</span>
                    </div>
                    <div className="tw-border-t tw-border-grey-lighter tw-mb-6"></div>
                    <div className="tw-font-semibold tw-font-styleSans tw-text-2xl tw-text-brand-navy tw-mb-3 tw-leading-none">
                      {translation('account')}
                    </div>
                    {userSignedIn && (
                      <>
                        <div className="tw-flex tw-items-center tw-mb-3">
                          <Link legacyBehavior prefetch={false} href={AppRoutes.chatRoute().toPath()}>
                            <a className="tw-text-brand-navy tw-text-lg tw-leading-none hover:tw-underline">
                              {translation('messages')}
                            </a>
                          </Link>
                          {hasUnreadMessage ? (
                            <div className="tw-bg-orange tw-h-2.5 tw-w-2.5 tw-rounded-full tw-ml-2 tw-mt-1"></div>
                          ) : null}
                        </div>
                        <div className="tw-mb-3">
                          <Link legacyBehavior prefetch={false} href={AppRoutes.accountTripsRoute().toPath()}>
                            <a className="tw-text-brand-navy tw-text-lg tw-leading-none hover:tw-underline">
                              {translation('bookings')}
                            </a>
                          </Link>
                        </div>
                        <div className="tw-mb-3">
                          <Link legacyBehavior prefetch={false} href={AppRoutes.accountBucketListRoute().toPath()}>
                            <a className="tw-text-brand-navy tw-text-lg tw-leading-none hover:tw-underline">
                              {translation('bucketLists')}
                            </a>
                          </Link>
                        </div>
                        <div className="tw-mb-3">
                          <Link legacyBehavior prefetch={false} href={AppRoutes.accountGiftCodesRoute().toPath()}>
                            <a className="tw-text-brand-navy tw-text-lg tw-leading-none hover:tw-underline">
                              {translation('yourGifts')}
                            </a>
                          </Link>
                        </div>
                        <div className="tw-mb-3">
                          <Link legacyBehavior prefetch={false} href={AppRoutes.accountCreditsRoute().toPath()}>
                            <a className="tw-text-brand-navy tw-text-lg tw-leading-none hover:tw-underline">
                              {translation('creditReferrals')}
                            </a>
                          </Link>
                        </div>
                        <div className="tw-mb-3">
                          <Link legacyBehavior prefetch={false} href={AppRoutes.accountProfileRoute().toPath()}>
                            <a className="tw-text-brand-navy tw-text-lg tw-leading-none hover:tw-underline">
                              {translation('manageAccount')}
                            </a>
                          </Link>
                        </div>
                        <div
                          className="tw-text-brand-navy tw-text-lg tw-leading-none tw-mt-3 tw-cursor-pointer"
                          onClick={async () => {
                            setShowSidenav(false);
                            setIsSelectedProfile(false);

                            startTransition(() => {
                              logout();
                            })

                            await interactionResponse()
                            const sideNavContent = document.querySelector('.sidenav__content');
                            if (sideNavContent) {
                              enableBodyScroll(sideNavContent);
                            }
                          }}
                        >
                          <a>{translation('logout')}</a>
                        </div>
                      </>
                    )}
                  </div>
                  <div className={classNames('tw-p-4', { 'tw-hidden': !isSelectedCurrency })}>
                    <div
                      className="tw-flex tw-items-center tw-cursor-pointer hover:tw-font-semibold tw-mb-6"
                      onClick={() => {
                        setIsSelectedCurrency(false);
                      }}
                    >
                      <FiChevronLeft size={18} className="tw-mr-2 tw-align-top" />
                      <span className="tw-text-sm tw-leading-4">{translation('backToHome')}</span>
                    </div>

                    <div className="tw-border-t tw-border-grey-lighter tw-mb-6"></div>

                    <div className="tw-font-semibold tw-font-styleSans tw-text-3xl tw-text-brand-navy tw-mb-3 tw-leading-none">
                      {translation('chooseCurrency')}
                    </div>

                    <div className="tw-font-semibold">
                      <ListCurrency />
                    </div>
                  </div>
                  <div
                    className={classNames('tw-pb-4', {
                      'tw-hidden':
                        isSelectedCountry ||
                        isSelectedAbout ||
                        isSelectedCurrency ||
                        isSelectedGift ||
                        isSelectedProfile,
                    })}
                  >
                    <div className="tw-px-4">
                    {initialData.settings.showBestOfListNav ? (
                        <div className="tw-mt-3">
                          <Link legacyBehavior href={AppRoutes.bestOfListsRoute().toPath()}>
                            <a className="tw-text-brand-navy tw-font-styleSans tw-text-2xl tw-font-semibold hover:tw-underline tw-leading-none">
                              {translation('bestOfSS')}
                            </a>
                          </Link>
                        </div>
                      ) : null}
                      <div
                        onClick={() => setIsSelectedGift(true)}
                        className="tw-flex tw-items-center tw-cursor-pointer tw-mt-3"
                      >
                        <span className="tw-text-brand-navy tw-text-2xl tw-font-semibold tw-font-styleSans tw-leading-none">
                          {translation('gifts')}
                        </span>
                        <FiChevronRight size={20} />
                      </div>

                      <div
                        onClick={() => setIsSelectedAbout(true)}
                        className="tw-flex tw-items-center tw-cursor-pointer tw-mt-3"
                      >
                        <span className="tw-text-brand-navy tw-text-2xl tw-font-semibold tw-font-styleSans tw-leading-none">
                          {translation('about')}
                        </span>
                        <FiChevronRight size={20} />
                      </div>

                      <div className="tw-mt-3">
                        <Link legacyBehavior prefetch={false} href={AppRoutes.magazinesRoute().toPath()}>
                          <a className="tw-text-brand-navy tw-font-styleSans tw-text-2xl tw-font-semibold hover:tw-underline tw-leading-none">
                            {translation('feelNotes')}
                          </a>
                        </Link>
                      </div>
                      <div className="tw-border-b tw-border-grey-lighter tw-border-solid tw-my-4"></div>
                      <div className="tw-mt-3">
                        {userSignedIn ? (
                          <div
                            onClick={() => setIsSelectedProfile(true)}
                            className="tw-flex tw-items-center tw-cursor-pointer tw-mb-3"
                          >
                            <span className="tw-text-brand-navy tw-text-2xl tw-font-semibold tw-font-styleSans tw-leading-none hover:tw-underline">
                              {translation('account')}
                            </span>
                            <FiChevronRight size={20} />
                          </div>
                        ) : (
                          <Link legacyBehavior prefetch={false} href={loginPath}>
                            <a className="tw-text-brand-navy tw-text-2xl tw-font-semibold tw-font-styleSans tw-leading-none hover:tw-underline">
                              {translation('login')}
                            </a>
                          </Link>
                        )}
                      </div>
                      {currentUser?.isAdmin && (
                        <div className="tw-mt-3">
                          <Link legacyBehavior prefetch={false} href={AdminRoutes.adminRoute().toUrl()}>
                            <a className="tw-text-brand-navy tw-text-2xl tw-font-semibold tw-font-styleSans tw-leading-none hover:tw-underline">
                              {translation('adminDashboard')}
                            </a>
                          </Link>
                        </div>
                      )}

                      {currentUser?.ambassadorProfile && currentUser.ambassadorProfile.status !== 'applied' && currentUser.ambassadorProfile.status !== 'rejected' ? (
                        <div className="tw-mt-3">
                          <Link legacyBehavior prefetch={false} href={AppRoutes.ambassadorDashboardRoute().toPath()}>
                            <a className="tw-text-brand-navy tw-text-2xl tw-font-semibold tw-font-styleSans tw-leading-none hover:tw-underline">
                              {translation('ambassadorDashboard')}
                            </a>
                          </Link>
                        </div>
                      ) : null}

                      {currentUser?.isPartner && (
                        <div className="tw-mt-3">
                          <Link legacyBehavior prefetch={false} href={AdminRoutes.partnerRoute().toUrl()}>
                            <a className="tw-text-brand-navy tw-text-2xl tw-font-semibold tw-font-styleSans tw-leading-none hover:tw-underline">
                              {translation('partnerDashboard')}
                            </a>
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ClientOnlyPortal>
    </>
  );
};

export default MobileSidenavAndButton;
